import { useEffect, useRef, useState, useCallback } from 'react';
import BpmnJS from 'bpmn-js/lib/Modeler';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';
import { Button } from './ui/button';
import { 
  Save, 
  Download, 
  Upload, 
  ZoomIn, 
  ZoomOut, 
  Maximize2,
  Minimize2,
  Undo, 
  Redo,
  RotateCcw,
  X
} from 'lucide-react';

const BpmnEditor = ({ 
  xml, 
  onSave, 
  onXmlChange, 
  readOnly = false,
  height = 600 
}) => {
  const containerRef = useRef(null);
  const modelerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentXml, setCurrentXml] = useState(xml);

  // Initialize modeler - re-run when fullscreen changes
  useEffect(() => {
    if (!containerRef.current) return;

    // Destroy previous modeler if exists
    if (modelerRef.current) {
      modelerRef.current.destroy();
    }

    const modeler = new BpmnJS({
      container: containerRef.current
    });

    modelerRef.current = modeler;

    // Load XML
    const loadDiagram = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const xmlToLoad = currentXml || xml;
        if (xmlToLoad) {
          const result = await modeler.importXML(xmlToLoad);
          if (result.warnings && result.warnings.length > 0) {
            console.warn('BPMN import warnings:', result.warnings);
          }
          const canvas = modeler.get('canvas');
          canvas.zoom('fit-viewport');
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load BPMN diagram:', err);
        if (!modelerRef.current?.get('canvas')) {
          setError('Failed to load diagram. Please check the XML format.');
        }
        setLoading(false);
      }
    };

    loadDiagram();

    // Track changes
    if (!readOnly) {
      modeler.on('commandStack.changed', async () => {
        try {
          const { xml: newXml } = await modeler.saveXML({ format: true });
          setCurrentXml(newXml);
          if (onXmlChange) {
            onXmlChange(newXml);
          }
        } catch (err) {
          console.error('Failed to save XML:', err);
        }
      });
    }

    return () => {
      modeler.destroy();
    };
  }, [isFullscreen]); // Re-initialize when fullscreen changes

  // Update currentXml when xml prop changes
  useEffect(() => {
    setCurrentXml(xml);
  }, [xml]);

  // Get current XML
  const getXml = useCallback(async () => {
    if (!modelerRef.current) return null;
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      return xml;
    } catch (err) {
      console.error('Failed to get XML:', err);
      return null;
    }
  }, []);

  // Save handler
  const handleSave = async () => {
    const currentXml = await getXml();
    if (currentXml && onSave) {
      onSave(currentXml);
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    if (!modelerRef.current) return;
    const canvas = modelerRef.current.get('canvas');
    canvas.zoom(canvas.zoom() * 1.2);
  };

  const handleZoomOut = () => {
    if (!modelerRef.current) return;
    const canvas = modelerRef.current.get('canvas');
    canvas.zoom(canvas.zoom() / 1.2);
  };

  const handleFitViewport = () => {
    if (!modelerRef.current) return;
    const canvas = modelerRef.current.get('canvas');
    canvas.zoom('fit-viewport');
  };

  const handleToggleFullscreen = async () => {
    // Save current XML before toggling
    if (modelerRef.current) {
      try {
        const { xml: savedXml } = await modelerRef.current.saveXML({ format: true });
        setCurrentXml(savedXml);
      } catch (e) {
        console.warn('Could not save XML before fullscreen toggle:', e);
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleResetView = () => {
    if (!modelerRef.current) return;
    const canvas = modelerRef.current.get('canvas');
    canvas.zoom(1);
    canvas.scroll({ dx: 0, dy: 0 });
  };

  // Undo/Redo
  const handleUndo = () => {
    if (!modelerRef.current) return;
    const commandStack = modelerRef.current.get('commandStack');
    commandStack.undo();
  };

  const handleRedo = () => {
    if (!modelerRef.current) return;
    const commandStack = modelerRef.current.get('commandStack');
    commandStack.redo();
  };

  // Export as SVG
  const handleExportSvg = async () => {
    if (!modelerRef.current) return;
    try {
      const { svg } = await modelerRef.current.saveSVG();
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'process-map.svg';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export SVG:', err);
    }
  };

  // Export as XML
  const handleExportXml = async () => {
    const currentXml = await getXml();
    if (!currentXml) return;
    
    const blob = new Blob([currentXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'process-map.bpmn';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Import XML
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.bpmn,.xml';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = event.target?.result;
        if (typeof content === 'string' && modelerRef.current) {
          try {
            await modelerRef.current.importXML(content);
            const canvas = modelerRef.current.get('canvas');
            canvas.zoom('fit-viewport');
            if (onXmlChange) {
              onXmlChange(content);
            }
          } catch (err) {
            console.error('Failed to import XML:', err);
            setError('Failed to import file. Please check the format.');
          }
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Fullscreen wrapper
  const editorContent = (
    <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 bg-slate-50 border-b border-slate-200 flex-wrap">
        {!readOnly && (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSave}
              data-testid="bpmn-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
            <div className="w-px h-6 bg-slate-300" />
            <Button variant="ghost" size="sm" onClick={handleUndo} title="Undo">
              <Undo className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleRedo} title="Redo">
              <Redo className="w-4 h-4" />
            </Button>
            <div className="w-px h-6 bg-slate-300" />
          </>
        )}
        
        <Button variant="ghost" size="sm" onClick={handleZoomIn} title="Zoom In">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleZoomOut} title="Zoom Out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleFitViewport} title="Fit to Viewport">
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleToggleFullscreen} 
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          data-testid="fullscreen-btn"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
        
        <div className="w-px h-6 bg-slate-300" />
        
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={handleImport} title="Import BPMN">
            <Upload className="w-4 h-4 mr-1" />
            Import
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={handleExportXml} title="Export BPMN">
          <Download className="w-4 h-4 mr-1" />
          Export BPMN
        </Button>
        <Button variant="ghost" size="sm" onClick={handleExportSvg} title="Export SVG">
          <Download className="w-4 h-4 mr-1" />
          Export SVG
        </Button>

        {isFullscreen && (
          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={handleToggleFullscreen} title="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Canvas */}
      <div className="relative flex-1 bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#235894]"></div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div className="text-red-500 text-center">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}
        <div 
          ref={containerRef} 
          className="w-full"
          style={{ height: isFullscreen ? 'calc(100vh - 60px)' : `${height}px` }}
          data-testid="bpmn-canvas"
        />
      </div>
    </div>
  );

  // Fullscreen modal overlay
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-white">
        {editorContent}
      </div>
    );
  }

  return editorContent;
};

export default BpmnEditor;
